import React, { useState, useEffect } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { useNavigate } from 'react-router-dom';
import { handleNavigation } from '@/utils/navigation-utils';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AuditorExportDialog } from '@/components/auditor-export/auditor-export-dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartColumn, faCircleQuestion, faChartPie, faProjectDiagram, faFileExport, faQuestion } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { getAvailableFeatures } from '@/utils/featuresUtil';

interface ReportLibraryCardProps {
  id: number;
  item: ReportLibraryCardsItem;
  onClick: (item: ReportLibraryCardsItem) => void;
}

const ReportLibraryCard = ({ id, item, onClick }: ReportLibraryCardProps) => {
  const iconMap = {
    faChartColumn: <FontAwesomeIcon icon={faChartColumn} />,
    faCircleQuestion: <FontAwesomeIcon icon={faCircleQuestion} />,
    faChartPie: <FontAwesomeIcon icon={faChartPie} />,
    faProjectDiagram: <FontAwesomeIcon icon={faProjectDiagram} />,
    faFileExport: <FontAwesomeIcon icon={faFileExport} />,
  };

  const icon = iconMap[item.icon] || <FontAwesomeIcon icon={faQuestion} />;

  const handleClick = () => {
    onClick(item);
  };

  return (
    <Card
      key={id}
      onClick={handleClick}
      className="cursor-pointer transition-all duration-300 hover:shadow-md border border-border hover:border-primary/30 overflow-hidden flex items-center"
    >
      <CardHeader className="flex flex-row items-center gap-4 px-4 w-full h-full">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-muted dark:bg-muted/40 flex items-center justify-center text-primary text-xl">
          {icon}
        </div>
        <div className="flex-1">
          <CardTitle className="text-lg md:text-base sm:text-sm whitespace-normal break-words line-clamp-2">
            {item.title}
          </CardTitle>
          <CardDescription className="mt-1 text-sm md:text-xs sm:text-[0.7rem] whitespace-normal break-words line-clamp-2">
            {item.subtitle}
          </CardDescription>
        </div>
        <div className="flex-shrink-0 text-primary">
          <i className="fa-duotone fa-arrow-right" aria-hidden="true" />
        </div>
      </CardHeader>
    </Card>
  );
};

interface ReportLibraryCardsItem {
  title: string;
  subtitle: string;
  description?: string | null;
  icon: 'faChartColumn' | 'faCircleQuestion' | 'faChartPie' | 'faProjectDiagram' | 'faFileExport';
  url?: string | null;
  routeName?: string | null;
  dialog?: 'auditor-export' | null;
}

const ReportLibrary: React.FC = () => {
  // const availableFeatures = getAvailableFeatures();
  const { activeTenancy } = useTenancy();
  const navigate = useNavigate();
  const [isAuditorExportOpen, setIsAuditorExportOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [reportCards, setReportCards] = useState<ReportLibraryCardsItem[]>([]);

  const handleCardClick = (item: ReportLibraryCardsItem) => {
    if (item.dialog === 'auditor-export') {
      setIsAuditorExportOpen(true);
    } else if (item.routeName) {
      handleNavigation(item.url, navigate, undefined, item.routeName);
    }
  };

  // Items for ISM Customers only
  const forISMCustomers: ReportLibraryCardsItem[] = [
    {
      title: 'RFFR Overview',
      subtitle: 'View your RFFR Overview',
      description: 'This overview provides a comprehensive understanding of your current RFFR implementation status.',
      routeName: 'rffrImplementationOverview.html',
      url: '/rffrImplementationOverview.html',
      icon: 'faChartPie'
    },
    {
      title: 'RFFR Project Overview',
      subtitle: 'Overview of your RFFR Project',
      description: 'This overview provides graphs and filters to aid in understanding your current RFFR Project status.',
      routeName: 'rffrProjectOverview.html',
      url: '/rffrProjectOverview.html',
      icon: 'faProjectDiagram',
    }
  ];

  useEffect(() => {
    if (!activeTenancy && ["", "Loading..."].includes(activeTenancy?.id)) {
      setIsLoading(true);
      return;
    }
    // Build the array of cards to display based on customer plan and features
    const forAllCustomers: ReportLibraryCardsItem[] = [];

    // Check if customer is on Starter plan (case-insensitive)
    const customerPlan = activeTenancy?.plan?.toLowerCase() || '';
    const isStarterPlan = customerPlan.includes('starter');

    // Add Reading Progress Report (exclude for Starter plan)
    if (!isStarterPlan) {
      forAllCustomers.push({
        title: 'Reading Progress Report',
        subtitle: 'View your reading progress',
        description: 'Use this report to view your users\' reading progress',
        routeName: 'reports',
        url: '/reports',
        icon: 'faChartColumn'
      });
    }

    // Add Auditor Pack if not disabled
    if (!activeTenancy?.disabledFeatures?.includes('auditorPack')) {
      forAllCustomers.push({
        title: 'Auditor Pack',
        subtitle: 'SOA and Assurance Task Export',
        icon: 'faFileExport',
        dialog: 'auditor-export',
      });
    }

    setReportCards([
      ...forAllCustomers,
      ...(activeTenancy.plan.toLowerCase() === 'ism' ? forISMCustomers : []),
    ]);
    setIsLoading(false);
  }, [activeTenancy]);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Report Library</h1>

      {isLoading ?
        (<p className="text-muted-foreground">Loading Report Library...</p>)
        :
        (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportCards.map((item, index) => (
            <ReportLibraryCard
              key={index}
              id={index}
              item={item}
              onClick={handleCardClick}
            />
          ))}
        </div>
        )}
      <AuditorExportDialog
        isOpen={isAuditorExportOpen}
        onClose={() => setIsAuditorExportOpen(false)}
        onExportComplete={() => {
          console.log('Export completed successfully');
          setIsAuditorExportOpen(false);
        }}
        onError={(error) => {
          console.error('Export error:', error);
        }}
      />
    </div>
  );
}

export default ReportLibrary;
